'use client';

import { type FC, useCallback, useState, useContext, useMemo } from 'react';
import { Dialog, Flex, Text, ButtonGroup, Button } from '@/_components';
import { DeleteMultipleItemsDialogProps } from './DeleteMultipleItemsDialog.types';
import { deleteDocument } from '@/_api/client-documents';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

export const DeleteMultipleItemsDialog: FC<DeleteMultipleItemsDialogProps> = ({
  companyId,
  currentPath,
  selectedDocumentItems,
  isOpen,
  onClose,
  onDeleted,
}) => {
  const { token, isAdvisor } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { preferences } = useContext(FirmSettingsContext);

  const [deleting, setDeleting] = useState(false);

  const onDelete = useCallback(async () => {
    try {
      setDeleting(true);

      for (const doc of selectedDocumentItems) {
        if (
          doc.name !== '_soraban' &&
          (!preferences.disable_folder_action ||
            isAdvisor ||
            doc.type === 'file')
        ) {
          await deleteDocument({
            documentName: doc.name,
            path: currentPath,
            companyId,
            isFolder: doc.type === 'folder',
            token,
          });
        }
      }

      await onDeleted();
      addToast('success', 'Deleted successfully');
    } catch (e) {
      addToast('error', 'Deleted Failed');
      console.error(e);
    } finally {
      setDeleting(false);
    }
  }, [
    selectedDocumentItems,
    currentPath,
    companyId,
    token,
    isAdvisor,
    addToast,
    onDeleted,
    preferences,
  ]);

  const itemCount = useMemo(() => {
    return selectedDocumentItems.length;
  }, [selectedDocumentItems]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Confirm Delete">
      <Dialog.Content>
        <Flex isColumn={true} gap={8}>
          <Text type="paragraph">
            Are you sure you want to delete{' '}
            <b>
              {itemCount} {itemCount === 1 ? 'item' : 'items'}
            </b>
            ?
          </Text>
          <ButtonGroup align="right">
            <Button
              value="Cancel"
              intent="secondary"
              onClick={onClose}
              isDisabled={deleting}
            />
            <Button
              value={deleting ? 'Deleting...' : 'Delete'}
              intent="danger"
              onClick={onDelete}
              isLoading={deleting}
              isDisabled={deleting}
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
